import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/knowledge.css';

const ICN = {
    search: (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    caret: (
        <svg viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    plus: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    upload: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 11V4m0 0L4 7m3-3l3 3M2 2h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    book: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M2 3a1 1 0 011-1h3.5A1.5 1.5 0 018 3.5V12a1 1 0 00-1-1H3a1 1 0 01-1-1V3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
            <path d="M12 3a1 1 0 00-1-1H7.5A1.5 1.5 0 006 3.5V12a1 1 0 011-1h4a1 1 0 001-1V3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
    ),
    check: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M2.5 7l3 3L11.5 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    x: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    ban: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.4" />
        </svg>
    ),
    lock: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <rect x="3" y="6" width="8" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.3" />
            <path d="M4.5 6V4.5a2.5 2.5 0 015 0V6" stroke="currentColor" strokeWidth="1.3" />
        </svg>
    ),
};

const STATUS_FILTERS = ['All terms', 'Approved', 'Pending', 'Forbidden', 'Rejected'];

const ghl = (text, q) => {
    if (!q.trim()) return text;
    const i = (text || '').toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <mark>{text.slice(i, i + q.length)}</mark>
            {text.slice(i + q.length)}
        </>
    );
};

const Rail = ({ glossaries, active, setActive }) => {
    const totalTerms = glossaries.reduce((s, g) => s + g.n, 0);
    return (
        <aside className="kb-rail">
            <div className="kb-rail-h">Glossaries<span className="ct">{glossaries.length}</span></div>
            <button
                type="button"
                className={`kb-rail-item ${active === 'all' ? 'on' : ''}`}
                onClick={() => setActive('all')}
            >
                <span className="kb-rail-flag">ALL</span>
                <span className="kb-rail-body">
                    <span className="kb-rail-name">All terms</span>
                    <span className="kb-rail-sub">Every glossary · {totalTerms}</span>
                </span>
            </button>
            {glossaries.map((g) => (
                <button
                    key={g.id}
                    type="button"
                    className={`kb-rail-item ${active === g.id ? 'on' : ''}`}
                    onClick={() => setActive(g.id)}
                >
                    <span className="kb-rail-flag">{g.flag}</span>
                    <span className="kb-rail-body">
                        <span className="kb-rail-name">{g.name}</span>
                        <span className="kb-rail-sub">{g.sub} · {g.n}</span>
                    </span>
                </button>
            ))}
            <div className="kb-rail-divide" />
            <button type="button" className="kb-rail-add">{ICN.plus} New glossary</button>
        </aside>
    );
};

const Stats = ({ totalTerms, approvedCount, pendingCount, forbiddenCount, glossaryCount }) => (
    <div className="kb-stats">
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic">{ICN.book}</span>Total terms</div>
            <div className="kb-stat-val">{totalTerms.toLocaleString()}</div>
            <div className="kb-stat-sub">across {glossaryCount} glossaries</div>
        </div>
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic green">{ICN.check}</span>Approved</div>
            <div className="kb-stat-val">
                {approvedCount}
                <span className="kb-stat-unit">{totalTerms ? Math.round((approvedCount / totalTerms) * 100) : 0}%</span>
            </div>
            <div className="kb-stat-sub">live in the editor</div>
        </div>
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic yellow">{ICN.book}</span>Pending review</div>
            <div className="kb-stat-val">{pendingCount}</div>
            <div className="kb-stat-sub">awaiting reviewer sign-off</div>
        </div>
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic indigo">{ICN.lock}</span>Forbidden</div>
            <div className="kb-stat-val">{forbiddenCount}</div>
            <div className="kb-stat-sub">flagged off-brand</div>
        </div>
    </div>
);

const Bar = ({ q, setQ, filter, setFilter, counts }) => (
    <div className="kb-bar-wrap">
        <div className="kb-bar">
            <div className="kb-search">
                {ICN.search}
                <input placeholder="Search terms, translations…" value={q} onChange={(e) => setQ(e.target.value)} />
                <span className="kbd">/</span>
            </div>
        </div>
        <div className="kb-tabs" role="tablist">
            {STATUS_FILTERS.map((f) => (
                <button
                    key={f}
                    type="button"
                    role="tab"
                    aria-selected={filter === f}
                    className={`kb-tab ${filter === f ? 'on' : ''}`}
                    onClick={() => setFilter(f)}
                >
                    {f}
                    <span className="kb-tab-ct">{counts[f] ?? 0}</span>
                </button>
            ))}
        </div>
    </div>
);

const statusFor = (term) => {
    if (term.ignore) return 'forbidden';
    if (term.approved) return 'approved';
    if (term.rejected) return 'rejected';
    return 'pending';
};

const TermRow = ({ term, q, glossaryName, glossaryId, navigate, canApprove, onApprove, onDecline, busy }) => {
    const status = statusFor(term);
    const showActions = status === 'pending' && canApprove;
    return (
        <div className="kb-gl-row">
            <div className="kb-gl-term">
                <b>{ghl(term.source, q)}</b>
                {term.context && <span className="pos">{term.context}</span>}
            </div>
            <div className="kb-gl-def">
                {term.target ? ghl(term.target, q) : <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>No translation</span>}
            </div>
            <div className="kb-gl-trans">
                {glossaryName && (
                    <button
                        type="button"
                        className="kb-gl-tag"
                        style={{ background: 'var(--bg-soft)', color: 'var(--ink-3)', border: 0, cursor: 'pointer' }}
                        onClick={() => navigate(`/dashboard/glossary/${glossaryId}/terms`)}
                    >
                        {glossaryName}
                    </button>
                )}
            </div>
            <div className="kb-gl-meta">
                {showActions ? (
                    <div className="kb-gl-actions">
                        <button
                            type="button"
                            className="kb-gl-act approve"
                            disabled={busy}
                            onClick={() => onApprove(glossaryId, term._id)}
                        >
                            {ICN.check}<span>Approve</span>
                        </button>
                        <button
                            type="button"
                            className="kb-gl-act decline"
                            disabled={busy}
                            onClick={() => onDecline(glossaryId, term._id)}
                        >
                            {ICN.x}<span>Decline</span>
                        </button>
                    </div>
                ) : (
                    <span className={`kb-gl-tag ${status}`}>
                        {status === 'approved' && ICN.check}
                        {status === 'forbidden' && ICN.ban}
                        {status === 'rejected' && ICN.x}
                        <span>{status}</span>
                    </span>
                )}
            </div>
        </div>
    );
};

const TermList = ({ rows, q, navigate, canApprove, onApprove, onDecline, busyId }) => (
    <div className="kb-tbl">
        <div className="kb-gl-row head">
            <span>Term</span>
            <span>Translation</span>
            <span>Glossary</span>
            <span className="r">Status</span>
        </div>
        {rows.length === 0 && (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                No terms match your search.
            </div>
        )}
        {rows.slice(0, 100).map((t, i) => (
            <TermRow
                key={`${t._glossaryId}-${t._id || i}`}
                term={t}
                q={q}
                glossaryName={t._glossaryName}
                glossaryId={t._glossaryId}
                navigate={navigate}
                canApprove={canApprove}
                onApprove={onApprove}
                onDecline={onDecline}
                busy={busyId === t._id}
            />
        ))}
    </div>
);

const GlossariesPage = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [glossaries, setGlossaries] = useState([]);
    const [active, setActive] = useState('all');
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('All terms');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Whether the current user may approve/decline terms. Defaults to false
    // until permissions load so review actions never flash for someone who
    // can't use them; the backend is still the authority.
    const [canApprove, setCanApprove] = useState(false);
    // _id of the term whose review action is in flight, to disable its buttons.
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/team/my-permissions', { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => {
                const perms = data.permissions || {};
                // Back-compat: missing approve flag falls back to canEditGlossary.
                const allowed = data.isOwner
                    || (perms.canApproveGlossary !== undefined ? perms.canApproveGlossary : perms.canEditGlossary);
                setCanApprove(!!allowed);
            })
            .catch(() => { /* leave approval disabled; server still enforces */ });
    }, [token]);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        axios
            .get('/api/glossary', { headers })
            .then(async (r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data.glossaries || []);
                // Fetch terms for each glossary so we can count + search
                const enriched = await Promise.all(list.map(async (g) => {
                    try {
                        const tr = await axios.get(`/api/glossary/${g._id}/terms`, { headers });
                        return { ...g, _terms: tr.data || [] };
                    } catch {
                        return { ...g, _terms: [] };
                    }
                }));
                setGlossaries(enriched);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load glossaries.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const railItems = useMemo(() => glossaries.map((g) => ({
        id: g._id,
        flag: ((g.targetLanguage || g.name || '?').slice(0, 2)).toUpperCase(),
        name: g.name,
        sub: `${(g.sourceLanguage || '?').toUpperCase()} → ${(g.targetLanguage || '?').toUpperCase()}`,
        n: (g._terms || []).length,
    })), [glossaries]);

    const allTerms = useMemo(() => glossaries.flatMap((g) => (g._terms || []).map((t) => ({
        ...t,
        _glossaryId: g._id,
        _glossaryName: g.name,
    }))), [glossaries]);

    const filteredTerms = useMemo(() => {
        let r = allTerms;
        if (active !== 'all') r = r.filter((t) => t._glossaryId === active);
        if (filter === 'Approved') r = r.filter((t) => statusFor(t) === 'approved');
        else if (filter === 'Pending') r = r.filter((t) => statusFor(t) === 'pending');
        else if (filter === 'Forbidden') r = r.filter((t) => statusFor(t) === 'forbidden');
        const s = q.trim().toLowerCase();
        if (s) r = r.filter((t) => (t.source || '').toLowerCase().includes(s) || (t.target || '').toLowerCase().includes(s));
        return r;
    }, [allTerms, active, filter, q]);

    const totalTerms = allTerms.length;
    const approvedCount = allTerms.filter((t) => statusFor(t) === 'approved').length;
    const pendingCount = allTerms.filter((t) => statusFor(t) === 'pending').length;
    const forbiddenCount = allTerms.filter((t) => statusFor(t) === 'forbidden').length;
    const rejectedCount = allTerms.filter((t) => statusFor(t) === 'rejected').length;

    const tabCounts = {
        'All terms': totalTerms,
        Approved: approvedCount,
        Pending: pendingCount,
        Forbidden: forbiddenCount,
        Rejected: rejectedCount,
    };

    // Patch a single term inside the nested glossaries → _terms structure.
    const applyTermUpdate = (glossaryId, termId, patch) => {
        setGlossaries((prev) => prev.map((g) => (g._id === glossaryId
            ? { ...g, _terms: (g._terms || []).map((t) => (t._id === termId ? { ...t, ...patch } : t)) }
            : g)));
    };

    const reviewTerm = async (glossaryId, termId, body) => {
        setBusyId(termId);
        try {
            const { data } = await axios.put(
                `/api/glossary/${glossaryId}/terms/${termId}`,
                body,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            applyTermUpdate(glossaryId, termId, data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update term.');
        } finally {
            setBusyId(null);
        }
    };

    // Approve clears any prior rejection; decline does the inverse.
    const handleApprove = (glossaryId, termId) => reviewTerm(glossaryId, termId, { approved: true, rejected: false });
    const handleDecline = (glossaryId, termId) => reviewTerm(glossaryId, termId, { rejected: true, approved: false });

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading glossaries…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Glossaries</h1>
                <span className="rs-page-title-count">{totalTerms.toLocaleString()}</span>
                <div className="rs-page-actions">
                    <button type="button" className="btn-q">{ICN.upload}<span>Import</span></button>
                    <button type="button" className="btn-p">{ICN.plus}<span>New glossary</span></button>
                </div>
            </div>
            <p className="rs-page-sub">Approved terms that lock your brand voice across every language.</p>

            <Stats
                totalTerms={totalTerms}
                approvedCount={approvedCount}
                pendingCount={pendingCount}
                forbiddenCount={forbiddenCount}
                glossaryCount={glossaries.length}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>
                <Rail glossaries={railItems} active={active} setActive={setActive} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Bar q={q} setQ={setQ} filter={filter} setFilter={setFilter} counts={tabCounts} />
                    <TermList
                        rows={filteredTerms}
                        q={q}
                        navigate={navigate}
                        canApprove={canApprove}
                        onApprove={handleApprove}
                        onDecline={handleDecline}
                        busyId={busyId}
                    />
                </div>
            </div>
        </>
    );
};

export default GlossariesPage;
